import React, { FC, useState } from 'react';
import { Box, ListItemText, Menu, MenuItem, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';

/**
 * The way into the computer based assessments from the blue header bar.
 *
 * Only the local O/L exam papers exist so far. The other entries are listed so
 * students can see what is coming, but they cannot be opened yet.
 */
type Entry = { label: string; url?: string; children?: Entry[] };

const boards: Entry[] = [
  {
    label: 'Local',
    children: [
      { label: 'Grade School' },
      {
        label: 'Ordinary Level',
        children: [{ label: 'Exam Papers', url: '/cba' }, { label: 'Mock Papers' }],
      },
      { label: 'Advanced Level' },
    ],
  },
  { label: 'Cambridge' },
  { label: 'Edexcel' },
];

/** A see-through, frosted panel so the page behind the header shows through. */
const paperSx = {
  mt: 0.5,
  minWidth: '150px',
  color: '#fff',
  backgroundColor: 'rgba(20, 24, 36, 0.45)',
  backdropFilter: 'blur(6px)',
  border: '1px solid rgba(255, 255, 255, 0.45)',
  borderRadius: '8px',
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)',
  '& .MuiList-root': { p: '4px' },
};

const itemSx = {
  fontSize: '14px',
  minHeight: 0,
  py: 0.5,
  px: 1,
  my: '2px',
  borderRadius: '6px',
  border: '1px solid transparent',
  '&:hover, &.Mui-selected, &.Mui-selected:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    borderColor: 'rgba(255, 255, 255, 0.35)',
  },
  // Coming soon entries still need to be readable over the photo.
  '&.Mui-disabled': { opacity: 0.85 },
};

/** One open submenu: the item it hangs off and what it lists. */
type Level = { anchor: HTMLElement; entries: Entry[] };

const CbaMenu: FC = () => {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [levels, setLevels] = useState<Level[]>([]);

  const close = () => {
    setLevels([]);
    setAnchor(null);
  };

  const open = (entry: Entry) => {
    if (entry.url) {
      close();
      navigate(entry.url);
    }
  };

  // Pointing at an item in menu `depth` closes every submenu deeper than it,
  // then opens its own if it has one.
  const point = (depth: number, entry: Entry, target: HTMLElement) =>
    setLevels((current) => {
      const kept = current.slice(0, depth);
      return entry.children ? [...kept, { anchor: target, entries: entry.children }] : kept;
    });

  const renderItem = (entry: Entry, depth: number) => {
    if (entry.children) {
      return (
        <MenuItem
          key={entry.label}
          sx={itemSx}
          selected={levels[depth]?.entries === entry.children}
          onMouseEnter={(event) => point(depth, entry, event.currentTarget)}
          onClick={(event) => point(depth, entry, event.currentTarget)}>
          <ListItemText primaryTypographyProps={{ fontSize: '14px' }}>{entry.label}</ListItemText>
          <ChevronRightIcon fontSize='small' />
        </MenuItem>
      );
    }

    return (
      <MenuItem
        key={entry.label}
        sx={itemSx}
        disabled={!entry.url}
        onMouseEnter={(event) => point(depth, entry, event.currentTarget)}
        onClick={() => open(entry)}>
        <ListItemText
          primaryTypographyProps={{ fontSize: '14px' }}
          secondary={entry.url ? undefined : 'Coming soon'}
          secondaryTypographyProps={{ fontSize: '10px', color: 'inherit', lineHeight: 1 }}>
          {entry.label}
        </ListItemText>
      </MenuItem>
    );
  };

  return (
    <>
      <Box
        component='button'
        type='button'
        aria-controls='cba-menu'
        aria-haspopup='true'
        aria-expanded={Boolean(anchor)}
        onClick={(event: React.MouseEvent<HTMLElement>) => setAnchor(event.currentTarget)}
        sx={{
          px: 1.5,
          ml: -1.5,
          mr: 2.5,
          py: 1,
          border: 0,
          cursor: 'pointer',
          color: '#fff',
          font: 'inherit',
          backgroundColor: anchor ? 'rgba(255, 255, 255, 0.12)' : 'transparent',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.12)' },
        }}>
        <Typography>CBA</Typography>
      </Box>

      <Menu
        id='cba-menu'
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: paperSx }}>
        {boards.map((entry) => renderItem(entry, 0))}
      </Menu>

      {levels.map((level, i) => (
        <Menu
          key={i}
          anchorEl={level.anchor}
          open
          onClose={close}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          // The menus before this one stay open behind it, so let the pointer reach them.
          sx={{ pointerEvents: 'none' }}
          PaperProps={{ sx: { ...paperSx, mt: '-5px', ml: '4px', pointerEvents: 'auto' } }}
          hideBackdrop
          disableAutoFocus
          disableEnforceFocus>
          {level.entries.map((entry) => renderItem(entry, i + 1))}
        </Menu>
      ))}
    </>
  );
};

export default CbaMenu;
